import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import TaskRow from './TaskRow';
import EmptyState from './EmptyState';

export default function TasksList({
  tasks, onChange, accountId = null, showAccount = false, emptyMessage = 'No tasks yet.',
}) {
  const [showNew, setShowNew] = useState(false);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);

  const create = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/admin/tasks', {
        title: title.trim(),
        priority,
        accountId: accountId || undefined,
      });
      onChange?.([res.task, ...tasks]);
      setTitle('');
      setShowNew(false);
    } catch (err) {
      // Surface via alert — keeps component simple; pages wrap their own toast.
      alert(err.message || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const complete = async (task) => {
    const res = await api.post(`/api/admin/tasks/${task.id}/complete`);
    onChange?.(tasks.map((t) => t.id === task.id ? res.task : t));
  };
  const remove = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    await api.delete(`/api/admin/tasks/${task.id}`);
    onChange?.(tasks.filter((t) => t.id !== task.id));
  };

  return (
    <div className="space-y-2">
      {tasks.length === 0 && !showNew && (
        <EmptyState
          title={emptyMessage}
          action={
            <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-900 bg-[#B0FC38]/80 hover:bg-[#B0FC38]">
              <Plus className="w-3 h-3" /> Add task
            </button>
          }
        />
      )}
      {tasks.map((t) => (
        <TaskRow key={t.id} task={t} onComplete={complete} onDelete={remove} showAccount={showAccount} />
      ))}
      {showNew ? (
        <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.08] bg-white/[0.02]">
          <input
            autoFocus
            className="flex-1 bg-transparent text-[13px] text-white placeholder-gray-500 focus:outline-none"
            placeholder="Task title…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') create(); if (e.key === 'Escape') setShowNew(false); }}
          />
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-1 text-[11px] text-white">
            <option value="low">low</option>
            <option value="normal">normal</option>
            <option value="high">high</option>
            <option value="urgent">urgent</option>
          </select>
          <button onClick={create} disabled={submitting || !title.trim()} className="px-2 py-1 rounded-lg text-[11px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">Add</button>
          <button onClick={() => setShowNew(false)} className="px-2 py-1 text-[11px] text-gray-500 hover:text-white">Cancel</button>
        </div>
      ) : tasks.length > 0 ? (
        <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.03]">
          <Plus className="w-3 h-3" /> Add task
        </button>
      ) : null}
    </div>
  );
}
